import { type RefObject, useCallback, useEffect, useState } from 'react'

interface ScrollOverflowState {
	hasOverflow: boolean
	isAtBottom: boolean
	hasScrolled: boolean
}

export default function useScrollOverflow(
	ref: RefObject<HTMLElement | null>,
	threshold: number = 20
): ScrollOverflowState {
	const [state, setState] = useState<ScrollOverflowState>({
		hasOverflow: false,
		isAtBottom: false,
		hasScrolled: false
	})

	const check = useCallback(() => {
		const el = ref.current
		if (!el) return

		const { scrollTop, scrollHeight, clientHeight } = el
		const hasOverflow = scrollHeight > clientHeight + 1 // 1px tolerance
		const isAtBottom = scrollTop + clientHeight >= scrollHeight - threshold
		const hasScrolled = scrollTop > 5

		setState((prev) => {
			if (
				prev.hasOverflow === hasOverflow &&
				prev.isAtBottom === isAtBottom &&
				prev.hasScrolled === hasScrolled
			) {
				return prev
			}
			return { hasOverflow, isAtBottom, hasScrolled }
		})
	}, [ref, threshold])

	useEffect(() => {
		const el = ref.current
		if (!el) return undefined

		check()

		el.addEventListener('scroll', check, { passive: true })
		window.addEventListener('resize', check)

		// Watch for content size changes via ResizeObserver
		const ro = new ResizeObserver(check)
		ro.observe(el)
		// Also observe the first child (the actual content wrapper) if it exists
		if (el.firstElementChild) {
			ro.observe(el.firstElementChild)
		}

		return () => {
			el.removeEventListener('scroll', check)
			window.removeEventListener('resize', check)
			ro.disconnect()
		}
	}, [ref, check])

	return state
}
